/**
 * The save popup and its root-mounted host (SPEC `saved-posts` § Save popup).
 *
 * One dialog for every surface. A feed card, a notification card, the preview popup and a Posts
 * table cell all call the same `openSave(postId)` — so the popup can be stacked over the preview
 * dialog, and so there is exactly one place that knows how a save is committed.
 *
 * Two rules the shape follows from:
 *
 *  - **Nothing is written mid-edit.** Checking a list, adding a tag and typing a note all stay in
 *    component state; one Save button commits the batch (`PUT`, or `DELETE` when Saved was
 *    unchecked). The Notifications-tab stance, for the same reason: a half-applied edit the user
 *    never confirmed is worse than no edit.
 *  - **"Saved" is the save row itself.** The first checkbox is not a list — it is the existence of
 *    the `saved_posts` row. Unchecking it kills everything under it, which is why the list rows
 *    below go disabled the moment it is off: they cannot outlive their parent.
 *
 * The one exception to "nothing mid-edit" is **+ New list**: a list is a shared vocabulary entity
 * and needs a server id before a checkbox can reference it, so it is created on the spot (#57) and
 * checked. Its *membership* still commits with the batch.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SAVED_LIST_NAME_MAX,
  SAVED_NOTE_MAX_CHARS,
  type SavedStateDTO,
} from '@lw/shared';
import { deletePostSaved, getPostSavedState, putPostSaved } from '../../api/saved';
import { QK } from '../../api/queryKeys';
import { describeError, isApiError } from '../../api/client';
import { Modal, PlusIcon, Spinner, cx, useToast } from '../../components';
import { patchSavedInCaches } from './savedCache';
import { TagInput } from './TagInput';
import { useCreateSavedList, useSavedListsQuery, useSavedTagsQuery } from './useSavedCollections';
import './saved.css';

/** Below this many characters left the counter appears; above it the field is quiet. */
const NOTE_COUNTER_THRESHOLD = 200;

type SaveDialogApi = {
  openSave: (postId: number) => void;
};

const SaveDialogContext = createContext<SaveDialogApi | null>(null);

export function useSaveDialog(): SaveDialogApi {
  const api = useContext(SaveDialogContext);
  if (api === null) throw new Error('useSaveDialog must be used inside <SaveDialogProvider>');
  return api;
}

/**
 * Root-mounted host, the `PostPreviewProvider` pattern: mounted once per screen, above everything
 * that can open it, so the dialog is never a child of the card that raised it.
 */
export function SaveDialogProvider({ children }: { children: ReactNode }) {
  const [postId, setPostId] = useState<number | null>(null);
  const openSave = useCallback((id: number) => setPostId(id), []);
  const api = useMemo<SaveDialogApi>(() => ({ openSave }), [openSave]);

  return (
    <SaveDialogContext.Provider value={api}>
      {children}
      {postId !== null ? <SaveDialog postId={postId} onClose={() => setPostId(null)} /> : null}
    </SaveDialogContext.Provider>
  );
}

/* ---------------------------------------------------------------- validation */

/** `{field: message}` from a 400's `details`; anything else surfaces as one dialog-level line. */
function fieldErrors(error: unknown): { fields: Record<string, string>; message: string | null } {
  if (!isApiError(error) || error.code !== 'validation_error') {
    return { fields: {}, message: null };
  }
  const details = error.details;
  if (typeof details === 'object' && details !== null && !Array.isArray(details)) {
    const fields: Record<string, string> = {};
    for (const [key, value] of Object.entries(details as Record<string, unknown>)) {
      if (typeof value === 'string') fields[key] = value;
    }
    if (Object.keys(fields).length > 0) return { fields, message: null };
  }
  return { fields: {}, message: error.message };
}

/* -------------------------------------------------------------------- dialog */

type SaveDialogProps = { postId: number; onClose: () => void };

function SaveDialog({ postId, onClose }: SaveDialogProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const state = useQuery({
    queryKey: QK.savedState(postId),
    queryFn: ({ signal }) => getPostSavedState(postId, signal),
    // The popup is the only writer, and it seeds its own draft from this — a refetch behind an open
    // dialog would silently disagree with what is on screen.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
  const lists = useSavedListsQuery();
  const tags = useSavedTagsQuery();
  const createList = useCreateSavedList();

  /* ------------------------------------------------------------- the draft */

  const [draft, setDraft] = useState<{
    saved: boolean;
    listIds: number[];
    tags: string[];
    note: string;
  } | null>(null);
  const [newListName, setNewListName] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Seeded once, from whatever #63 answered. A first save opens with "Saved" already checked: the
  // button that opened this dialog is a *save* button, so the default is the thing it promises.
  useEffect(() => {
    if (draft !== null || state.data === undefined) return;
    const loaded: SavedStateDTO = state.data;
    setDraft(
      loaded.saved
        ? { saved: true, listIds: [...loaded.listIds], tags: loaded.tags.map((tag) => tag.name), note: loaded.note ?? '' }
        : { saved: true, listIds: [], tags: [], note: '' },
    );
  }, [draft, state.data]);

  const existing = state.data?.saved === true;
  const busy = state.isPending || draft === null;

  /* ------------------------------------------------------------- committing */

  const commit = useMutation({
    mutationFn: async () => {
      if (draft === null) return null;
      if (existing && !draft.saved) {
        await deletePostSaved(postId);
        return null;
      }
      return putPostSaved(postId, {
        listIds: draft.listIds,
        tags: draft.tags,
        note: draft.note.trim().length === 0 ? null : draft.note,
      });
    },
    onSuccess: (result) => {
      // Patch, never invalidate: the surfaces that show this post are ordered lists, and refetching
      // them would move the ground under a reader who only bookmarked something.
      const saved = result !== null && result.saved;
      patchSavedInCaches(queryClient, {
        postId,
        saved: result !== null,
        savedAt: saved ? result.savedAt : null,
        savedAtDisplay: saved ? result.savedAtDisplay : null,
        // Server-built, list names and the card-row date spelling included (§12.5).
        savedInfo: saved ? result.savedInfo : null,
      });
      // The counts moved and the `/saved` collection itself changed, so *that* tree does refetch.
      void queryClient.invalidateQueries({ queryKey: QK.saved });
      onClose();
    },
    onError: (error) => {
      const parsed = fieldErrors(error);
      setErrors(parsed.fields);
      setFormError(parsed.message);
      // A validation failure belongs beside the field that caused it; anything else is transport.
      if (Object.keys(parsed.fields).length === 0 && parsed.message === null) {
        toast.show({ tone: 'error', message: describeError(error) });
      }
    },
  });

  const patch = (next: Partial<NonNullable<typeof draft>>) => {
    setDraft((current) => (current === null ? current : { ...current, ...next }));
    setErrors({});
    setFormError(null);
  };

  const toggleList = (listId: number) => {
    if (draft === null) return;
    patch({
      listIds: draft.listIds.includes(listId)
        ? draft.listIds.filter((id) => id !== listId)
        : [...draft.listIds, listId],
    });
  };

  const submitNewList = () => {
    const name = (newListName ?? '').trim();
    if (name.length === 0) {
      setNewListName(null);
      return;
    }
    createList.mutate(name, {
      onSuccess: (list) => {
        setNewListName(null);
        setErrors({});
        setDraft((current) =>
          current === null ? current : { ...current, listIds: [...current.listIds, list.id] },
        );
      },
      onError: (error) => {
        const parsed = fieldErrors(error);
        setErrors(
          Object.keys(parsed.fields).length > 0
            ? { newList: parsed.fields.name ?? Object.values(parsed.fields)[0] ?? '' }
            : { newList: describeError(error) },
        );
      },
    });
  };

  /* ----------------------------------------------------------------- render */

  const noteLeft = SAVED_NOTE_MAX_CHARS - (draft?.note.length ?? 0);
  const unsaving = existing && draft?.saved === false;

  return (
    <Modal
      open
      size="sm"
      title={existing ? 'Edit save' : 'Save post'}
      onClose={onClose}
      className="save-dialog"
      footer={
        <div className="save-dialog__foot">
          {formError !== null ? <span className="field__error">{formError}</span> : null}
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={unsaving ? 'btn-danger' : 'btn-primary'}
            disabled={busy || commit.isPending}
            onClick={() => commit.mutate()}
          >
            {commit.isPending ? <Spinner size="sm" /> : null}
            {unsaving ? 'Remove from saved' : 'Save'}
          </button>
        </div>
      }
    >
      {busy ? <Spinner size="lg" label="Loading…" /> : null}

      {draft !== null ? (
        <>
          <div className="save-dialog__lists">
            {/* The save row itself, not a list. Unchecking it is the unsave confirmation. */}
            <label className="checklist__item save-dialog__root">
              <input
                type="checkbox"
                checked={draft.saved}
                // A first save cannot start by not saving: the checkbox exists here only to say
                // what the button is about to do.
                disabled={!existing}
                onChange={(event) => patch({ saved: event.target.checked })}
              />
              <span className="checklist__label">Saved</span>
              <span className="checklist__hint">
                {existing ? 'Uncheck to remove' : 'All saved posts'}
              </span>
            </label>

            <div className={cx('save-dialog__custom', !draft.saved && 'is-disabled')}>
              {(lists.data?.lists ?? []).map((list) => (
                <label className="checklist__item" key={list.id}>
                  <input
                    type="checkbox"
                    checked={draft.listIds.includes(list.id)}
                    disabled={!draft.saved}
                    onChange={() => toggleList(list.id)}
                  />
                  <span className="checklist__label">{list.name}</span>
                  <span className="checklist__hint">{list.postCount}</span>
                </label>
              ))}

              {newListName === null ? (
                <button
                  type="button"
                  className="btn-ghost btn-ghost--sm save-dialog__new"
                  disabled={!draft.saved}
                  onClick={() => setNewListName('')}
                >
                  <PlusIcon size={12} />
                  New list
                </button>
              ) : (
                <div className="save-dialog__new-row">
                  <input
                    className="input"
                    autoFocus
                    value={newListName}
                    maxLength={SAVED_LIST_NAME_MAX}
                    placeholder="List name"
                    aria-label="New list name"
                    onChange={(event) => setNewListName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        submitNewList();
                      }
                      if (event.key === 'Escape') {
                        event.stopPropagation();
                        setNewListName(null);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn-primary btn-primary--sm"
                    disabled={createList.isPending}
                    onClick={submitNewList}
                  >
                    Add
                  </button>
                </div>
              )}
              {errors.newList !== undefined ? (
                <span className="field__error">{errors.newList}</span>
              ) : null}
              {errors.listIds !== undefined ? (
                <span className="field__error">{errors.listIds}</span>
              ) : null}
            </div>
          </div>

          <div className={cx('field', !draft.saved && 'is-disabled')}>
            <span className="field__label">Tags</span>
            <TagInput
              value={draft.tags}
              onChange={(next) => patch({ tags: next })}
              vocabulary={tags.data?.tags ?? []}
              disabled={!draft.saved}
            />
            {errors.tags !== undefined ? <span className="field__error">{errors.tags}</span> : null}
          </div>

          <div className={cx('field', !draft.saved && 'is-disabled')}>
            <label className="field__label" htmlFor="save-note">
              Note
            </label>
            <textarea
              id="save-note"
              className="input save-dialog__note"
              rows={3}
              value={draft.note}
              maxLength={SAVED_NOTE_MAX_CHARS}
              disabled={!draft.saved}
              placeholder="Why did you save this?"
              onChange={(event) => patch({ note: event.target.value })}
            />
            {noteLeft <= NOTE_COUNTER_THRESHOLD ? (
              <span className="field__hint">{noteLeft} characters left</span>
            ) : null}
            {errors.note !== undefined ? <span className="field__error">{errors.note}</span> : null}
          </div>
        </>
      ) : null}
    </Modal>
  );
}

export default SaveDialogProvider;
